import {
    Box, Grid, IconButton, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { iconSize } from 'assets/theme/iconSize';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { formatPhone } from '@functions/format-phone';
import { formatCnpj } from '@functions/brazil-documents';
import { apiErrorMessage } from '@functions/api-error-message';
import { CompanyResponse } from '../services/responses/company.response';
import { PagedResponse } from 'types/paged-response';
import { CompanyService } from '../services/company.service';

interface Props {
    data: PagedResponse<CompanyResponse>;
    editAction: (id: string) => void;
    refresh: boolean;
    setRefresh: (v: boolean) => void;
}

const COLUMNS = 6;

export function CompaniesTable({ data, editAction, refresh, setRefresh }: Props) {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert, alertDelete } = useAlert();

    const records: CompanyResponse[] = data?.data?.records ?? [];

    /// As pessoas da empresa continuam cadastradas: o servidor só as desliga.
    function doDelete(id: string) {
        setLoading(dispatch, true);
        new CompanyService()
            .deleteById(id)
            .then(() => {
                showAlert(t('companies.excluirSucesso'), 'success', 2500);
                setRefresh(!refresh);
            })
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 3000))
            .finally(() => setLoading(dispatch, false));
    }

    return (
        <TableContainer sx={{ marginTop: '15px', maxHeight: '60vh', overflowY: 'auto' }}>
            <Table size="medium">
                <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'background.card' }}>
                    <TableRow hover>
                        <TableCell width="24%">{t('companies.razaoSocial')}</TableCell>
                        <TableCell width="18%">{t('companies.nomeFantasia')}</TableCell>
                        <TableCell width="16%">{t('companies.cnpj')}</TableCell>
                        <TableCell width="14%">{t('people.telefone')}</TableCell>
                        <TableCell width="18%">{t('people.email')}</TableCell>
                        <TableCell width="10%" align="center">{t('general.acoes')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!data ? (
                        Array.from({ length: 5 }).map((_, index) => (
                            <TableRow key={index}>
                                {Array.from({ length: COLUMNS }).map((__, cell) => (
                                    <TableCell key={cell}><Skeleton height={28} /></TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : records.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={COLUMNS} align="center">{t('companies.nenhuma')}</TableCell>
                        </TableRow>
                    ) : (
                        records.map((company) => (
                            <TableRow key={company.id} hover>
                                <TableCell>{company.name}</TableCell>
                                <TableCell>{company.tradeName || '-'}</TableCell>
                                <TableCell>{company.cnpj ? formatCnpj(company.cnpj) : '-'}</TableCell>
                                <TableCell>{company.phone ? formatPhone(company.phone) : '-'}</TableCell>
                                <TableCell>{company.email || '-'}</TableCell>
                                <TableCell align="center">
                                    <Grid container justifyContent="center">
                                        <Tooltip title={t('general.editar')}>
                                            <IconButton size="small" sx={{ p: 0.25 }} onClick={() => editAction(company.id)}>
                                                <Box component={EditIcon} sx={iconSize} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('general.excluir')}>
                                            <IconButton size="small" sx={{ p: 0.25 }} onClick={() => alertDelete(company.id, doDelete, company.name)}>
                                                <Box component={DeleteIcon} sx={{ ...iconSize, color: 'error.main' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
